import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { DataTable } from '@/components/ui/data-table';
import { useAuditLog } from '@/hooks/useAuditLog';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import {
  FileText,
  RefreshCw,
  Search,
  Download,
  Eye,
  Clock,
  User,
  Package,
  ShoppingCart,
  Settings,
  CheckCircle,
  XCircle,
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const actionIcons: Record<string, React.ElementType> = {
  create: Plus,
  update: Edit,
  delete: Trash2,
  view: Eye,
  approve: CheckCircle,
  reject: XCircle,
  login: User,
  logout: User,
  export: Download,
  submit: ShoppingCart,
};

const actionColors: Record<string, string> = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  view: 'bg-gray-100 text-gray-700',
  approve: 'bg-green-100 text-green-700',
  reject: 'bg-red-100 text-red-700',
  login: 'bg-purple-100 text-purple-700',
  logout: 'bg-gray-100 text-gray-700',
  export: 'bg-cyan-100 text-cyan-700',
  submit: 'bg-yellow-100 text-yellow-700',
};

const categoryIcons: Record<string, React.ElementType> = {
  inventory: Package,
  requisition: ShoppingCart,
  po: FileText,
  vendor: User,
  settings: Settings,
  user: User,
  system: Settings,
  budget: FileText,
  approval: CheckCircle,
  return: Package,
};

export default function AuditLogPage() {
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [selectedEntry, setSelectedEntry] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  const { getAuditLogs, getSummary, getAvailableActions, exportAuditLogs } = useAuditLog();

  const { data: logsData, isLoading, error, refetch } = getAuditLogs({
    action: actionFilter !== 'all' ? actionFilter : undefined,
    category: categoryFilter !== 'all' ? categoryFilter : undefined,
    search: searchQuery || undefined,
    page: 1,
    page_size: 50,
  });

  const { data: summary } = getSummary();
  const { data: actionsData } = getAvailableActions();
  const exportMutation = exportAuditLogs();

  const logs = logsData?.entries || [];
  const actions = actionsData?.actions || [];
  const categories = actionsData?.categories || [];

  const toggleExpand = (entryId: string) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId);
    } else {
      newExpanded.add(entryId);
    }
    setExpandedEntries(newExpanded);
  };

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      const result = await exportMutation.mutateAsync({
        filters: {
          action: actionFilter !== 'all' ? actionFilter : undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
        },
        format,
      });

      if (format === 'csv' && result.data.content) {
        const blob = new Blob([result.data.content], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        a.click();
      }

      toast.success('Export complete', `Audit log exported as ${format.toUpperCase()}.`);
    } catch {
      toast.error('Export failed', 'Failed to export audit log.');
    }
  };

  const openDetail = (entryId: string) => {
    setSelectedEntry(entryId);
    setIsDetailOpen(true);
  };

  const selectedLog = logs.find((l) => l.entry_id === selectedEntry);

  if (error) {
    return (
      <div className="space-y-6">
        <div className="p-4 rounded-lg bg-red-50 border-2 border-red-200">
          <div className="flex items-center gap-3">
            <XCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-900 font-semibold">Error loading audit log</p>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-red-800 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">Audit Log</h1>
          <p className="text-text-secondary mt-1">Complete activity tracking and compliance records</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => handleExport('csv')}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <p className="text-sm text-blue-600">Today</p>
            <p className="text-3xl font-bold text-blue-700">{summary?.total_entries_today || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-4">
            <p className="text-sm text-purple-600">This Week</p>
            <p className="text-3xl font-bold text-purple-700">{summary?.total_entries_week || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-4">
            <p className="text-sm text-green-600">By Action</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {Object.entries(summary?.entries_by_action || {})
                .slice(0, 3)
                .map(([action, count]) => (
                  <Badge key={action} variant="outline" className="text-xs">
                    {action}: {count}
                  </Badge>
                ))}
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-4">
            <p className="text-sm text-orange-600">By Category</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {Object.entries(summary?.entries_by_category || {})
                .slice(0, 3)
                .map(([cat, count]) => (
                  <Badge key={cat} variant="outline" className="text-xs">
                    {cat}: {count}
                  </Badge>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All Activity</TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="approvals">Approvals</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
                  <Input
                    placeholder="Search by record identifier..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    {actions.map((action) => (
                      <SelectItem key={action.value} value={action.value}>
                        {action.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          {isLoading ? (
            <TableSkeleton rows={15} columns={5} />
          ) : logs.length > 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  {logs.map((entry) => {
                    const ActionIcon = actionIcons[entry.action] || Eye;
                    const CategoryIcon = categoryIcons[entry.category] || FileText;
                    const isExpanded = expandedEntries.has(entry.entry_id);
                    const hasChanges = entry.changed_fields && entry.changed_fields.length > 0;

                    return (
                      <div key={entry.entry_id} className="border rounded-lg overflow-hidden">
                        <div
                          className={cn(
                            'flex items-center gap-4 p-4 cursor-pointer hover:bg-background-subtle transition-colors',
                            hasChanges && 'bg-yellow-50'
                          )}
                          onClick={() => (hasChanges ? toggleExpand(entry.entry_id) : openDetail(entry.entry_id))}
                        >
                          <div className={cn('p-2 rounded-lg', actionColors[entry.action] || 'bg-gray-100')}>
                            <ActionIcon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {entry.action.toUpperCase()}
                              </Badge>
                              <Badge variant="secondary" className="text-xs">
                                {entry.category}
                              </Badge>
                              {entry.table_name && (
                                <span className="text-xs text-text-muted">{entry.table_name}</span>
                              )}
                            </div>
                            <p className="text-sm font-medium truncate mt-1">
                              {entry.record_identifier || entry.record_id || entry.entry_id}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm text-text-muted">
                              {format(new Date(entry.created_at), 'MMM d, yyyy HH:mm')}
                            </p>
                            {entry.user_name && <p className="text-xs text-text-muted">{entry.user_name}</p>}
                          </div>
                          {hasChanges && (
                            <Button variant="ghost" size="sm">
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                          )}
                        </div>

                        {isExpanded && hasChanges && (
                          <div className="border-t p-4 bg-background-subtle">
                            <p className="text-sm font-medium mb-2">Changed Fields:</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-text-muted mb-1">Changed:</p>
                                <div className="flex flex-wrap gap-1">
                                  {entry.changed_fields?.map((field) => (
                                    <Badge key={field} variant="outline" className="text-xs">
                                      {field}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                              {entry.old_value && (
                                <div>
                                  <p className="text-xs text-text-muted mb-1">Old Value:</p>
                                  <pre className="text-xs bg-white p-2 rounded border overflow-auto max-h-24">
                                    {JSON.stringify(entry.old_value, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {entry.new_value && (
                                <div>
                                  <p className="text-xs text-text-muted mb-1">New Value:</p>
                                  <pre className="text-xs bg-white p-2 rounded border overflow-auto max-h-24">
                                    {JSON.stringify(entry.new_value, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary">No audit entries found</p>
                <p className="text-sm text-text-muted mt-1">Activity will appear here as actions are performed.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="inventory">
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 text-text-muted mx-auto mb-4" />
              <p className="text-text-secondary">Inventory audit trail</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approvals">
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-12 w-12 text-text-muted mx-auto mb-4" />
              <p className="text-text-secondary">Approval audit trail</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card>
            <CardContent className="py-12 text-center">
              <User className="h-12 w-12 text-text-muted mx-auto mb-4" />
              <p className="text-text-secondary">User activity audit trail</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Entry Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Audit Entry Details</DialogTitle>
            <DialogDescription>{selectedLog?.entry_id}</DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-text-muted">Action</p>
                  <Badge className={actionColors[selectedLog.action]}>{selectedLog.action}</Badge>
                </div>
                <div>
                  <p className="text-sm text-text-muted">Category</p>
                  <Badge variant="secondary">{selectedLog.category}</Badge>
                </div>
                <div>
                  <p className="text-sm text-text-muted">Table</p>
                  <p className="font-medium">{selectedLog.table_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-text-muted">Record ID</p>
                  <p className="font-medium font-mono text-sm">{selectedLog.record_id || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-text-muted">User</p>
                  <p className="font-medium">{selectedLog.user_name || selectedLog.user_id || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-text-muted">IP Address</p>
                  <p className="font-medium font-mono text-sm">{selectedLog.ip_address || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-text-muted">Timestamp</p>
                  <p className="font-medium">
                    {format(new Date(selectedLog.created_at), 'PPpp')}
                    {' ('}
                    {formatDistanceToNow(new Date(selectedLog.created_at), { addSuffix: true })}
                    {')'}
                  </p>
                </div>
              </div>

              {selectedLog.changed_fields && selectedLog.changed_fields.length > 0 && (
                <div>
                  <p className="text-sm text-text-muted mb-2">Changed Fields</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedLog.changed_fields.map((field) => (
                      <Badge key={field} variant="outline">
                        {field}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedLog.old_value && (
                <div>
                  <p className="text-sm text-text-muted mb-2">Old Value</p>
                  <pre className="text-xs bg-gray-50 p-3 rounded border overflow-auto max-h-48">
                    {JSON.stringify(selectedLog.old_value, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.new_value && (
                <div>
                  <p className="text-sm text-text-muted mb-2">New Value</p>
                  <pre className="text-xs bg-gray-50 p-3 rounded border overflow-auto max-h-48">
                    {JSON.stringify(selectedLog.new_value, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
